import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import * as Util from '../../../common/utilities';
import { getBase64 } from '../../../utilities/objectUtils';

import { Button, Spinner } from '@salesforce/design-system-react';
import { ImageContent } from '../images/illustrations/experiment-tips';
import { ExperimentEditStep, ImageImportModal, ImagePanel } from '../';

import './ExperimentEditImage.scss';

class ExperimentEditImage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      type: {
        inputValue: ''
      },
      showUpload: !(
        this.props.modals?.experimentWizard.data?.experiment?.image_link ||
        this.props.modals?.experimentWizard.data?.experiment?.image_url
      ),
      contentBuilderPreviewAsset: undefined,
      imageLoadError: undefined,
      dragging: false,
      url: undefined,
      urlSubmit: false,
      showNewSelectableRow: false
    };
    this.fields = [];
    this.inputFileRef = React.createRef();
  }

  resetImage() {
    this.setState({
      showUpload: !this.state.showUpload,
      url: undefined,
      dragging: false,
      contentBuilderPreviewAsset: undefined
    });
  }

  componentDidUpdate(prevProps) {
    if (this.state.contentBuilderPreviewAsset) {
      const cbPreviewPath = `contentBuilder.data.previews.${this.state.contentBuilderPreviewAsset.id}.isLoading`;
      if (Util.propChanged(prevProps, this.props, cbPreviewPath, false)) {
        const previewId = this.state.contentBuilderPreviewAsset.id;
        const preview = this.props.contentBuilder.data.previews?.[previewId];
        const { error, dataUri } = preview || {};
        if (error) {
          this.resetImage();
          // display error
          this.props.actions.notifyApiError({
            apiError: { code: 'PREVIEW_FAILED' }
          });
        } else if (dataUri) {
          // preview is done loading
          const contentBuilderAsset = this.state.contentBuilderPreviewAsset;
          const details = {
            contentBuilderAsset,
            content_builder_id: contentBuilderAsset.id,
            content_builder_type: contentBuilderAsset.type
          };
          this.setState({
            contentBuilderPreviewAsset: {
              ...this.state.contentBuilderPreviewAsset,
              dataUri
            }
          });
          this.handleImageUpdate(dataUri, true, details);
        }
      }
    }
  }

  getI18nID(suffix) {
    return `components.experiment.experimentWizard.${suffix}`;
  }

  /**
   * Creates an Image control for the given field metadata
   *
   * @param data
   *   value: current string value
   *   field: internal name of field in redux
   *   custom: true if the field is a custom field, false otherwise
   *   label: when custom === false, id to use for translation of the field label
   *          when custom === true, the untranslatable label for the custom field
   * @returns <Input /> component
   */
  createImage(data) {
    const { formatMessage } = this.props.intl;
    const dragText = formatMessage({
      id: 'components.experiment.experimentWizard.dragText'
    });
    const dropText = (
      <Button
        className="link-span"
        assistiveText={{ label: 'Upload' }}
        iconCategory="utility"
        iconName="upload"
        label={formatMessage({
          id: 'components.experiment.experimentWizard.uploadText'
        })}
        onClick={() => this.inputFileRef.current.click()}
      />
    );

    const previewFile = (
      <img
        className="experiment-image-preview"
        style={{ display: 'none' }}
        src={this.state.url}
        alt=""
        onLoad={this.handleImageSuccess.bind(this)}
        onError={this.handleImageError.bind(this)}
      />
    );

    const chooseImageControls = (
      <div>
        <div
          onDrop={this.handleDrop.bind(this)}
          onDragOver={this.handleDragOver.bind(this)}
          onDragEnter={this.handleDragEnter.bind(this)}
          onDragLeave={this.handleDragLeave.bind(this)}
        >
          {dropText}
          <span className="link-span"> {dragText}</span>
        </div>
        <input
          type="file"
          id="open-file-input"
          ref={this.inputFileRef}
          style={{ display: 'none' }}
          multiple
          onChange={this.handleFileSelect.bind(this)}
          onClick={event => {
            event.target.value = null;
          }}
          accept="image/x-png,image/gif,image/jpeg"
        />
      </div>
    );

    return (
      <div className="image-url slds-col" key={`${data.field}-input`}>
        {this.state.url ? previewFile : null}
        <div className="experiment-input-container">{chooseImageControls}</div>
      </div>
    );
  }

  handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      this.setState({ dragging: true });
    }
  }
  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.setState({ dragging: false });
    }
  }
  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }
  handleDrop(e) {
    const validTypes = ['image/png', 'image/gif', 'image/jpeg'];
    const files = e.dataTransfer.files;
    if (files && files.length) {
      for (let i = 0; i < files.length; i++) {
        if (validTypes.includes(files[i].type)) {
          getBase64(files[i], droppedFileBase64 => {
            this.setState({
              showUpload: false,
              urlSubmit: true,
              url: droppedFileBase64
            });

            const image = new Image();
            image.src = droppedFileBase64;

            this.handleImageUpdate(droppedFileBase64);
          });
          e.dataTransfer.clearData();
        }
      }
      this.dragCounter = 0;
    }
    e.preventDefault();
    e.stopPropagation();
  }
  handleFileSelect(event) {
    const files = event.target.files;
    if (files && files.length) {
      for (let i = 0; i < files.length; i++) {
        getBase64(files[i], chosenFileBase64 => {
          this.setState({
            showUpload: false,
            urlSubmit: true,
            url: chosenFileBase64,
            showNewSelectableRow: true
          });

          const image = new Image();
          image.src = chosenFileBase64;

          this.handleImageUpdate(chosenFileBase64);
        });
      }
    }
  }

  handleImageSuccess() {
    this.setState({
      imageLoadError: false
    });
  }
  handleImageError() {
    const { formatMessage } = this.props.intl;
    this.setState({
      imageLoadError: true
    });
    this.props.actions.updateModal('experimentWizard', {
      errors: {
        image_url: formatMessage({
          id: 'components.experiment.experimentImageURLModal.notfound'
        })
      }
    });
  }
  handleImageUpdate(url, completed = true, details = {}) {
    const experiment = this.props.modals?.experimentWizard?.data?.experiment;
    const selectables =
      this.props.modals?.experimentWizard?.data?.experiment
        ?.content_test_assets || [];
    const selectable = { name: '', redirect_url: '' };
    if (url.startsWith('data:image')) {
      selectable.image_attributes = { image_data: url };
    } else {
      selectable.image_attributes = { image_url: url };
    }
    if (details['contentBuilderAsset']) {
      const { contentBuilderAsset: { name } = {} } = details;
      const { content_builder_id = {} } = details;
      selectable.name = name;
      selectable.image_attributes.content_builder_id = content_builder_id;
    } else if (details['ecsAsset']) {
      const { ecsAsset: { key } = {} } = details;
      const { ecsAsset: { url } = {} } = details;
      selectable.name = key;
      selectable.redirect_url = url;
    }

    selectables.push(selectable);

    experiment.content_test_assets = selectables;
    const update = {
      experiment: {
        ...experiment
      }
    };
    if (this.props.modals?.imageImport?.isOpen) {
      this.props.actions.closeModal('imageImport');
    }
    this.props.actions.updateModal('experimentWizard', update);
    this.setState({
      url,
      showUpload: false
    });
  }

  removeSelectable = () => {
    this.setState({
      showNewSelectableRow: false
    });
  };

  handleDelete = selectable => {
    const experiment = this.props.modals?.experimentWizard?.data?.experiment;
    const selectables = experiment?.content_test_assets || [];
    const index = selectable[0];
    const element = selectable[1];

    //remove unsaved image from selectables Object
    if (!element.experiment_uuid) {
      //this.removeSelectable();
      selectables.splice(index, 1);

      //add delete key to selectables Object to be deleted
    } else {
      selectables[index]._destroy = true;
    }
    experiment.content_test_assets = [...selectables];
    const update = {
      experiment: {
        ...experiment
      }
    };

    this.props.actions.updateModal('experimentWizard', update);
  };

  handleDisable = selectable => {
    const experiment = this.props.modals?.experimentWizard?.data?.experiment;
    const selectables = experiment?.content_test_assets || [];
    const index = selectable[0];
    const toggleStatus =
      selectables[index].status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    selectables[index].status = toggleStatus;

    experiment.content_test_assets = [...selectables];
    const update = {
      experiment: {
        ...experiment
      }
    };

    this.props.actions.updateModal('experimentWizard', update);
  };

  handleContentBuilderSelect(contentBuilderAsset) {
    const details = {
      contentBuilderAsset,
      content_builder_id: contentBuilderAsset.id,
      content_builder_type: 'image'
    };

    if (contentBuilderAsset.type === 'Image') {
      // Image types use publishedURL
      if (contentBuilderAsset.fileProperties?.publishedURL) {
        this.handleImageUpdate(
          contentBuilderAsset.fileProperties.publishedURL,
          true,
          details
        );
      }
    } else if (contentBuilderAsset.type === 'Image Block') {
      // Image Blocks have <img src="{URL}" /> tags in the contents
      const regex = new RegExp('src="(.*?)"');
      const content = contentBuilderAsset.content;
      this.handleImageUpdate(content.match(regex)[1], true, details);
    } else {
      details.content_builder_type = 'html';
      // HTML/Template blocks need to render a preview from server
      this.props.actions.fetchContentBuilderPreview(contentBuilderAsset.id);
      this.setState({ contentBuilderPreviewAsset: contentBuilderAsset });
    }

    if (contentBuilderAsset) {
      this.props.actions.clearContentBuilderSearch();
    }
  }

  handleEcsSelect(ecsAsset) {
    // Remove caching query parameter ?v=111
    const url = ecsAsset.image_link.split('?')[0];
    if (url) {
      const details = { ecsAsset };
      this.handleImageUpdate(url, true, details);
    }
  }

  renderTips() {
    const { formatMessage } = this.props.intl;
    const tipTitle = formatMessage({
      id: this.getI18nID('step1.title')
    });
    const tip1 = formatMessage({
      id: this.getI18nID('step1.tip1')
    });
    const tip2 = formatMessage({
      id: this.getI18nID('step1.tip2')
    });
    return (
      <div className="tips slds-col">
        <div className="tip-image">
          <ImageContent />
        </div>
        <p>{tipTitle}</p>
        <ul>
          <li>{tip1}</li>
          <li>{tip2}</li>
        </ul>
      </div>
    );
  }

  getSelectables() {
    const selectables =
      this.props.modals?.experimentWizard?.data?.experiment
        ?.content_test_assets || [];
    if (selectables.length) {
      return selectables.filter(function(selectable) {
        return selectable['_destroy'] !== true;
      });
    }
    return [];
  }

  renderSpinner() {
    return (
      <div className="spinner">
        <Spinner
          size="large"
          variant="brand"
          assistiveText={{ label: 'Large spinner' }}
        />
      </div>
    );
  }

  render() {
    const { actions, experiment } = this.props;
    const { formatMessage } = this.props.intl;
    const data = [
      [
        {
          type: 'string',
          field: 'image_url',
          label: 'components.experiment.experimentWizard.fields.pasteUrl',
          tooltip:
            'components.experiment.experimentWizard.fields.tooltip.imageUrl',
          value: experiment.image_url || ''
        }
      ]
    ];

    const selectables = this.getSelectables();
    const cbPreviewPath =
      this.props.contentBuilder?.data?.previews?.[
        this.state.contentBuilderPreviewAsset?.id
      ]?.isLoading || false;
    const imagePayloadLimit = this.props.errors?.imagePayloadLimit
      ? formatMessage({
          id: 'components.experiment.experimentWizard.imagePayloadLimit'
        })
      : null;

    return (
      <div className="experiment-container slds-grid">
        <ExperimentEditStep actions={actions} canNav={true} step="step1" />
        <div className="image slds-col">
          <p className="image-container-title">
            {formatMessage({ id: this.getI18nID('titles.image') })}
          </p>
          <div className="image-test">{this.createImage(data)}</div>
          <div>
            <Button
              label={formatMessage({
                id: this.getI18nID('importImageModal.uploadOptions')
              })}
              variant="base"
              onClick={() => this.props.actions.openModal('imageImport')}
            />
          </div>
          {cbPreviewPath ? (
            this.renderSpinner()
          ) : (
            <ImagePanel
              actions={this.props.actions}
              assets={selectables}
              showNewSelectableRow={this.state.showNewSelectableRow}
              onSelectableChange={this.props.onFieldChange.bind(this)}
              onDeleteSelectable={this.handleDelete}
              onDisableSelectable={this.handleDisable}
              status={experiment.status}
              history={this.props.history}
              modals={this.props.modals}
            />
          )}
          <p className="upload-error">{imagePayloadLimit}</p>
        </div>
        {this.renderTips()}
        <ImageImportModal
          actions={this.props.actions}
          assets={this.props.assets}
          contentBuilder={this.props.contentBuilder}
          modals={this.props.modals}
          onUrlSelect={this.handleImageUpdate.bind(this)}
          onContentBuilderSelect={this.handleContentBuilderSelect.bind(this)}
          onECSAssetSelect={this.handleEcsSelect.bind(this)}
        />
      </div>
    );
  }
}

ExperimentEditImage.propTypes = {
  actions: PropTypes.object,
  assets: PropTypes.object,
  contentBuilder: PropTypes.object,
  experiments: PropTypes.object,
  modals: PropTypes.object,
  experiment: PropTypes.object,
  onFieldChange: PropTypes.func.isRequired,
  intl: intlShape.isRequired
};

export default injectIntl(ExperimentEditImage);
